"use client";

import * as React from "react";
import { Check, ChevronsUpDown } from "lucide-react";

import { cn } from "@/lib/utils";
import { Button } from "@/components/ui/button";
import { Command, CommandEmpty, CommandGroup, CommandInput, CommandItem, CommandList } from "@/components/ui/command";
import { Popover, PopoverContent, PopoverTrigger } from "@/components/ui/popover";
import { getCampaign } from "@/sideEffects/api";
import { useQuery } from "@tanstack/react-query";
import { selectActiveFirm } from "@/store/appSelectors";
import { useSelector } from "react-redux";
import { LoadingSmall } from "@/components/LoadingSmall";
import { Campaign } from "@/lib/types";
import { useTranslation } from "react-i18next";

interface Props {
  onChange: (campaign: Campaign) => void;
  selectedCampaignGuid?: string;
  customFilter?: (campaign: Campaign) => boolean;
}

export const CampaignSelectGuid: React.FC<Props> = ({ onChange, selectedCampaignGuid, customFilter }) => {
  const { t } = useTranslation();
  const [open, setOpen] = React.useState(false);
  const activeFirm = useSelector(selectActiveFirm);

  const { data, isLoading } = useQuery({
    queryKey: ["getCampaign", activeFirm?.firmGuid],
    queryFn: () =>
      getCampaign(
        {
          numberOfObjectsPerPage: 99999999,
          pageNumber: 1,
        },
        {
          Firm_Id: String(activeFirm?.firmGuid),
        }
      ),
  });

  const filtered = customFilter ? (data?.data?.result || []).filter(customFilter) : data?.data?.result || [];

  return (
    <Popover open={open} onOpenChange={setOpen}>
      <PopoverTrigger asChild>
        {isLoading ? (
          <LoadingSmall />
        ) : (
          <Button variant="outline" role="combobox" aria-expanded={open} className="w-[200px] justify-between">
            {selectedCampaignGuid
              ? filtered.find((campaign) => campaign.id === selectedCampaignGuid)?.name
              : t("campaign.selectCampaign")}
            <ChevronsUpDown className="opacity-50" />
          </Button>
        )}
      </PopoverTrigger>
      <PopoverContent className="p-0">
        <Command>
          <CommandInput placeholder={t("dataTable.filterPlaceholder")} />
          <CommandList>
            <CommandEmpty>{t("dataTable.noResults")}</CommandEmpty>
            <CommandGroup>
              {filtered.map((campaign) => (
                <CommandItem
                  key={campaign.id}
                  value={campaign.id}
                  onSelect={(currentValue) => {
                    setOpen(false);
                    const campaign = filtered.find((c) => c.id === currentValue);
                    if (campaign) {
                      onChange(campaign);
                    }
                  }}
                >
                  {campaign.name}
                  <Check
                    className={cn("ml-auto", selectedCampaignGuid === campaign.id ? "opacity-100" : "opacity-0")}
                  />
                </CommandItem>
              ))}
            </CommandGroup>
          </CommandList>
        </Command>
      </PopoverContent>
    </Popover>
  );
};
